/**
 * <subcollection-filter>
 *
 * Makes the subcollection cards behave like toggleable filters:
 *   - Clicking a card that is not the collection being viewed navigates to it
 *     (default link behaviour) and remembers the collection it was clicked from.
 *   - Clicking the card of the collection currently being viewed navigates back
 *     to that remembered collection, falling back to the parent collection URL
 *     rendered server-side in `data-back-url`.
 *   - Cards rendered with `data-no-deselect` (parents opted out of the toggle,
 *     e.g. "Работно облекло") never navigate back — the click is swallowed and
 *     nothing happens.
 *
 * The origin is kept in sessionStorage as { from, to } and is only honoured when
 * `to` matches the page we are on, so a stale entry can never hijack a link.
 */
const SUBCOLLECTION_FILTER_ORIGIN_KEY = 'subcollection-filter:origin';

class SubcollectionFilter extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest('[data-subcollection-link]');
    if (!link || !this.contains(link)) return;

    if (link.dataset.current === 'true') {
      // Swallow the click either way — the card points at the page we are on, so
      // falling through to the href would just reload it and read as "nothing
      // happened".
      event.preventDefault();

      if (link.dataset.noDeselect === 'true') return;

      const backUrl = this.backUrl();
      if (!backUrl) return;

      this.clearOrigin();
      window.location.href = backUrl;
      return;
    }

    this.storeOrigin(link.getAttribute('href'));
  }

  /**
   * Where a second click on the active card should lead: the collection the user
   * came from, or the parent collection when there is nothing remembered.
   */
  backUrl() {
    const origin = this.readOrigin();
    if (origin && origin.from && origin.to === window.location.pathname) return origin.from;
    return this.dataset.backUrl || '';
  }

  /**
   * When the page itself is one of the listed subcollections, switching to a
   * sibling must keep the original parent as the origin — not the sibling page.
   */
  storeOrigin(href) {
    if (!href) return;

    const isSubcollectionPage = this.querySelector('[data-current="true"]') !== null;
    const current = window.location.pathname + window.location.search;
    const from = isSubcollectionPage ? this.backUrl() || current : current;

    try {
      const to = new URL(href, window.location.origin);
      sessionStorage.setItem(SUBCOLLECTION_FILTER_ORIGIN_KEY, JSON.stringify({ from, to: to.pathname }));
    } catch (error) {
      // Invalid href or sessionStorage unavailable (private mode) — links still work.
    }
  }

  readOrigin() {
    try {
      return JSON.parse(sessionStorage.getItem(SUBCOLLECTION_FILTER_ORIGIN_KEY)) ?? null;
    } catch (error) {
      return null;
    }
  }

  clearOrigin() {
    try {
      sessionStorage.removeItem(SUBCOLLECTION_FILTER_ORIGIN_KEY);
    } catch (error) {
      // Ignore — nothing to clean up.
    }
  }
}

customElements.define('subcollection-filter', SubcollectionFilter);
